import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, Send, X, Sparkles, Users, Store, ImagePlus, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { api } from '../services/api';
import { toast } from 'sonner';

// 📸 Composant pour sélectionner et compresser une image
const ImageUploader = ({ onImageSelect, disabled }) => {
  const fileInputRef = useRef(null);
  
  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Seules les images sont autorisées');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image trop lourde (max 5MB)');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = async (event) => {
      const img = new window.Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const maxSize = 600;
        let width = img.width;
        let height = img.height;
        
        if (width > height && width > maxSize) {
          height = (height * maxSize) / width;
          width = maxSize;
        } else if (height > maxSize) {
          width = (width * maxSize) / height;
          height = maxSize;
        }
        
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        const compressedBase64 = canvas.toDataURL('image/jpeg', 0.7);
        onImageSelect(compressedBase64);
      };
      img.src = event.target.result;
    };
    reader.readAsDataURL(file);
    
    if (fileInputRef.current) fileInputRef.current.value = '';
  };
  
  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
      />
      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        disabled={disabled}
        className="p-2 rounded-lg hover:bg-white/20 text-cyan-400 transition-colors disabled:opacity-50"
        title="Envoyer une photo"
      >
        <ImagePlus className="w-5 h-5" />
      </button>
    </>
  );
};

export default function LPEChat({ customerId, customerName, isOpen, onClose }) {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('team'); // 'team' ou 'community'
  const messagesEndRef = useRef(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [selectedImage, setSelectedImage] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);

  // Charger les messages
  useEffect(() => {
    if (isOpen) {
      loadMessages();
      const interval = setInterval(loadMessages, 5000); // Refresh toutes les 5s
      return () => clearInterval(interval);
    }
  }, [isOpen, activeTab]);

  const loadMessages = async () => {
    try {
      const data = await api.getChatMessages(customerId, activeTab);
      setMessages(data.messages || []);
      setUnreadCount(data.unreadCount || 0);
    } catch (error) {
      console.error('Erreur chargement messages:', error);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() && !selectedImage) return;
    
    setIsLoading(true);
    try {
      await api.sendChatMessage({
        customerId,
        customerName,
        message: newMessage,
        channel: activeTab,
        source: 'widget',  // Identifie que ça vient du widget en bas de page
        imageBase64: selectedImage || undefined,
      });
      setNewMessage('');
      setSelectedImage(null);
      loadMessages();
    } catch (error) {
      toast.error('💬 Message non envoyé. Vérifiez votre connexion.');
    } finally {
      setIsLoading(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        className="fixed bottom-20 right-4 z-50 w-80 sm:w-96"
      >
        {/* Conteneur avec effet scintillant ULTRA */}
        <motion.div 
          className="relative rounded-3xl overflow-hidden"
          animate={{
            boxShadow: [
              '0 0 20px rgba(139, 92, 246, 0.5), 0 0 40px rgba(236, 72, 153, 0.3), 0 0 60px rgba(59, 130, 246, 0.2)',
              '0 0 30px rgba(236, 72, 153, 0.5), 0 0 50px rgba(59, 130, 246, 0.3), 0 0 70px rgba(139, 92, 246, 0.2)',
              '0 0 25px rgba(59, 130, 246, 0.5), 0 0 45px rgba(139, 92, 246, 0.3), 0 0 65px rgba(236, 72, 153, 0.2)',
              '0 0 20px rgba(139, 92, 246, 0.5), 0 0 40px rgba(236, 72, 153, 0.3), 0 0 60px rgba(59, 130, 246, 0.2)',
            ],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
          style={{
            background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.4) 0%, rgba(236, 72, 153, 0.4) 50%, rgba(59, 130, 246, 0.4) 100%)',
            padding: '3px',
          }}
        >
          {/* Effet de particules scintillantes AMÉLIORÉ */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {[...Array(30)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute rounded-full"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  width: `${2 + Math.random() * 4}px`,
                  height: `${2 + Math.random() * 4}px`,
                  background: ['#fff', '#FFD700', '#00FFFF', '#FF69B4', '#8B5CF6'][Math.floor(Math.random() * 5)],
                }}
                animate={{
                  opacity: [0, 1, 0.5, 1, 0],
                  scale: [0, 1.5, 1, 1.5, 0],
                  x: [0, Math.random() * 20 - 10, 0],
                  y: [0, Math.random() * 20 - 10, 0],
                }}
                transition={{
                  duration: 2 + Math.random() * 3,
                  repeat: Infinity,
                  delay: Math.random() * 3,
                  ease: 'easeInOut',
                }}
              />
            ))}
          </div>

          {/* Animation de bordure lumineuse qui tourne */}
          <motion.div
            className="absolute inset-0 rounded-3xl pointer-events-none"
            style={{
              background: 'conic-gradient(from 0deg, transparent, rgba(255,255,255,0.5), transparent, rgba(139,92,246,0.5), transparent)',
            }}
            animate={{
              rotate: [0, 360],
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: 'linear',
            }}
          />

          {/* Effet de vague lumineuse */}
          <motion.div
            className="absolute inset-0 rounded-3xl pointer-events-none"
            style={{
              background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.3) 50%, transparent 100%)',
              backgroundSize: '200% 100%',
            }}
            animate={{
              backgroundPosition: ['200% 0', '-200% 0'],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'linear',
            }}
          />

          {/* Contenu principal */}
          <div className="relative bg-gradient-to-br from-slate-900 via-purple-900/50 to-slate-900 rounded-3xl">
            {/* Header avec effet brillant */}
            <div className="p-4 border-b border-purple-500/30 flex items-center justify-between relative overflow-hidden">
              {/* Effet de brillance dans le header */}
              <motion.div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background: 'linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.1) 50%, transparent 70%)',
                  backgroundSize: '200% 200%',
                }}
                animate={{
                  backgroundPosition: ['200% 200%', '-100% -100%'],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: 'linear',
                }}
              />
              
              <div className="flex items-center gap-2 relative z-10">
                <motion.div
                  animate={{ 
                    rotate: [0, 15, -15, 0],
                    scale: [1, 1.2, 1],
                  }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <Sparkles className="w-6 h-6 text-yellow-400 drop-shadow-[0_0_8px_rgba(250,204,21,0.8)]" />
                </motion.div>
                <motion.h3 
                  className="font-bold text-white text-lg"
                  animate={{
                    textShadow: [
                      '0 0 10px rgba(139,92,246,0.5)',
                      '0 0 20px rgba(236,72,153,0.5)',
                      '0 0 10px rgba(59,130,246,0.5)',
                      '0 0 10px rgba(139,92,246,0.5)',
                    ],
                  }}
                  transition={{ duration: 3, repeat: Infinity }}
                >
                  ✨ Chat LPE ✨
                </motion.h3>
                {unreadCount > 0 && (
                  <motion.span 
                    className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full"
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ duration: 0.5, repeat: Infinity }}
                  >
                    {unreadCount}
                  </motion.span>
                )}
              </div>
              <button
                onClick={onClose}
                className="p-1 rounded-full hover:bg-white/10 transition-colors relative z-10"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-purple-500/30">
              <button
                onClick={() => setActiveTab('team')}
                className={`flex-1 py-2 px-4 text-sm font-medium transition-colors flex items-center justify-center gap-1 ${
                  activeTab === 'team'
                    ? 'text-cyan-400 border-b-2 border-cyan-400'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                <Store className="w-4 h-4" />
                L&apos;Équipe
              </button>
              <button
                onClick={() => setActiveTab('community')}
                className={`flex-1 py-2 px-4 text-sm font-medium transition-colors flex items-center justify-center gap-1 ${
                  activeTab === 'community'
                    ? 'text-pink-400 border-b-2 border-pink-400'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                <Users className="w-4 h-4" />
                Communauté
              </button>
            </div>

            {/* Messages */}
            <div className="h-64 overflow-y-auto p-4 space-y-3">
              {messages.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  <MessageCircle className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">
                    {activeTab === 'team' 
                      ? 'Posez vos questions à l\'équipe !'
                      : 'Discutez avec la communauté !'}
                  </p>
                </div>
              ) : (
                messages.map((msg, index) => (
                  <motion.div
                    key={msg.id || index}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`flex ${msg.isOwn ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                        msg.isOwn
                          ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white'
                          : msg.isTeamMessage
                            ? 'bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-500/30 text-white'
                            : 'bg-white/10 text-white'
                      }`}
                    >
                      {!msg.isOwn && (
                        <div className={`text-xs font-semibold mb-1 ${
                          msg.isTeamMessage ? 'text-yellow-400' : 'text-pink-400'
                        }`}>
                          {msg.isTeamMessage ? '🏪 ' : ''}{msg.senderName}
                        </div>
                      )}
                      {/* Afficher l'image si présente */}
                      {msg.imageBase64 && (
                        <div 
                          className="mb-2 cursor-pointer"
                          onClick={() => setPreviewImage(msg.imageBase64)}
                        >
                          <img 
                            src={msg.imageBase64} 
                            alt="Photo partagée"
                            className="max-w-full rounded-lg hover:opacity-90 transition-opacity"
                          />
                        </div>
                      )}
                      {msg.message && <p className="text-sm">{msg.message}</p>}
                      <div className="text-[10px] text-white/50 mt-1 text-right">
                        {new Date(msg.createdAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                  </motion.div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Prévisualisation image sélectionnée */}
            {selectedImage && (
              <div className="px-4 py-2 border-t border-purple-500/30 bg-black/30">
                <div className="flex items-center gap-2">
                  <img 
                    src={selectedImage} 
                    alt="Aperçu" 
                    className="w-12 h-12 object-cover rounded-lg border border-purple-500/30"
                  />
                  <span className="flex-1 text-xs text-gray-400">Photo prête</span>
                  <button
                    onClick={() => setSelectedImage(null)}
                    className="p-1 rounded-lg hover:bg-red-500/20 text-red-400"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {/* Input avec bouton photo */}
            <div className="p-4 border-t border-purple-500/30">
              <div className="flex gap-2 items-center">
                <ImageUploader onImageSelect={setSelectedImage} disabled={isLoading} />
                <Input
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                  placeholder="Votre message..."
                  className="flex-1 bg-white/10 border-purple-500/30 text-white placeholder:text-gray-500"
                  disabled={isLoading}
                />
                <Button
                  onClick={sendMessage}
                  disabled={isLoading || (!newMessage.trim() && !selectedImage)}
                  className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600"
                >
                  {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
        
        {/* Modal prévisualisation image */}
        <AnimatePresence>
          {previewImage && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 p-4"
              onClick={() => setPreviewImage(null)}
            >
              <button
                onClick={() => setPreviewImage(null)}
                className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white"
              >
                <X className="w-6 h-6" />
              </button>
              <img
                src={previewImage}
                alt="Image agrandie"
                className="max-w-full max-h-full object-contain rounded-lg"
                onClick={(e) => e.stopPropagation()}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </AnimatePresence>
  );
}

// Bouton flottant pour ouvrir le chat - ULTRA SCINTILLANT
export function ChatButton({ onClick, unreadCount = 0 }) {
  return (
    <motion.button
      onClick={onClick}
      className="fixed bottom-4 right-4 z-40 w-16 h-16 rounded-full shadow-lg flex items-center justify-center"
      style={{
        background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 50%, #3B82F6 100%)',
      }}
      whileHover={{ scale: 1.15 }}
      whileTap={{ scale: 0.9 }}
      animate={{
        boxShadow: [
          '0 0 20px rgba(139, 92, 246, 0.6), 0 0 40px rgba(236, 72, 153, 0.4), 0 0 60px rgba(59, 130, 246, 0.2)',
          '0 0 30px rgba(236, 72, 153, 0.6), 0 0 50px rgba(59, 130, 246, 0.4), 0 0 70px rgba(139, 92, 246, 0.2)',
          '0 0 25px rgba(59, 130, 246, 0.6), 0 0 45px rgba(139, 92, 246, 0.4), 0 0 65px rgba(236, 72, 153, 0.2)',
          '0 0 20px rgba(139, 92, 246, 0.6), 0 0 40px rgba(236, 72, 153, 0.4), 0 0 60px rgba(59, 130, 246, 0.2)',
        ],
        scale: [1, 1.05, 1],
      }}
      transition={{
        boxShadow: {
          duration: 3,
          repeat: Infinity,
          ease: 'easeInOut',
        },
        scale: {
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut',
        },
      }}
    >
      {/* Anneau lumineux qui tourne */}
      <motion.div
        className="absolute inset-[-4px] rounded-full pointer-events-none"
        style={{
          background: 'conic-gradient(from 0deg, transparent, rgba(255,255,255,0.8), transparent, rgba(139,92,246,0.8), transparent)',
        }}
        animate={{
          rotate: [0, 360],
        }}
        transition={{
          duration: 3,
          repeat: Infinity,
          ease: 'linear',
        }}
      />
      
      {/* Effet de pulsation radial */}
      <motion.div
        className="absolute inset-0 rounded-full"
        animate={{
          background: [
            'radial-gradient(circle, rgba(255,255,255,0.4) 0%, transparent 60%)',
            'radial-gradient(circle, rgba(255,255,255,0) 0%, transparent 60%)',
          ],
        }}
        transition={{
          duration: 1,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />

      {/* Particules scintillantes autour du bouton */}
      {[...Array(8)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-2 h-2 rounded-full"
          style={{
            background: ['#FFD700', '#00FFFF', '#FF69B4', '#fff'][i % 4],
            top: '50%',
            left: '50%',
          }}
          animate={{
            x: [0, Math.cos(i * Math.PI / 4) * 35, 0],
            y: [0, Math.sin(i * Math.PI / 4) * 35, 0],
            opacity: [0, 1, 0],
            scale: [0, 1, 0],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            delay: i * 0.25,
            ease: 'easeInOut',
          }}
        />
      ))}
      
      <motion.div
        animate={{ rotate: [0, 10, -10, 0] }}
        transition={{ duration: 1.5, repeat: Infinity }}
      >
        <MessageCircle className="w-7 h-7 text-white relative z-10 drop-shadow-[0_0_8px_rgba(255,255,255,0.8)]" />
      </motion.div>
      
      {/* Badge de messages non lus */}
      {unreadCount > 0 && (
        <motion.span
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-white text-xs font-bold flex items-center justify-center"
        >
          {unreadCount > 9 ? '9+' : unreadCount}
        </motion.span>
      )}
    </motion.button>
  );
}
